import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import PropTypesConfig from 'config/prop-types';
import MeasureCard from 'components/measureCard';
import RaceCard from 'components/raceCard';
import RaceGroup from 'components/raceGroup';
import RaceGroupNav from 'components/raceGroupNav';
import SearchFilter from 'components/searchFilter';
import isInNXSWidgetArea from 'util/isInNXSWidgetArea';
import hasResultsGroups from 'services/hasResultsGroups';

// Styled components.
import styles from './styles';

import sendCustomEvent from '../../util/sendCustomEvent';

// Max sidebar width.
const HUB_MAIN_CONTENT = 876;

// Max result count for larger hubs.
const MAX_RESULT_COUNT = 4;

/**
 * A component to handle the display of an Election Hub widget.
 */
export default class ElectionHubWidget extends React.PureComponent {
  /**
   * Define PropTypes for this component.
   * @type {object}
   */
  static propTypes = {
    description: PropTypes.string,
    results: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape(PropTypesConfig.MEASURE_SHAPE),
        PropTypes.shape(PropTypesConfig.RACE_SHAPE),
      ]),
    ),
    title: PropTypes.string,
    widgetId: PropTypes.string.isRequired,
  };

  /**
   * Define default props for this component.
   * @type {object}
   */
  static defaultProps = {
    description: '',
    results: [],
    title: '',
  };

  /**
   * Set Initial State
   * @type {object}
   */
  state = {
    width: 300,
    searchedTerm: null,
  };

  /**
   * Add window resize listener on mount.
   * Get initial element width.
   */
  componentDidMount() {
    // Trigger function on mount.
    this.handleResize();
    this.sendDisplayTrackingEvent();

    // Start event listener.
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Remove resize listener on unmount.
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  /**
   * Resize handler for hub.
   */
  handleResize = () => {
    const element = ReactDOM.findDOMNode(this.hubTarget); // eslint-disable-line react/no-find-dom-node

    this.refCallback(element);
  }

  /**
  * Get width of element.
  *
  * @param {object} element - referenced element.
  * @returns {string} width - width of element.
  */
  handleWidthFetch = (element) => {
    // Set default width. Assume sidebar or mobile.
    let containerWidth = 300;

    // If we have an element, get measurements.
    if (element) {
      // Get bounding rect.
      const measurements = element.getBoundingClientRect();

      // Make sure we have a measurement object.
      if (measurements) {
        const {
          width,
        } = measurements;

        // Set new value of container width.
        containerWidth = width;
      }
    }

    return containerWidth;
  }

  /**
   * Reference callback for Card.
   * Get width of element on resize.
   *
   * @param {object} element - referenced element.
   */
  refCallback = (element) => {
    // Get width from element.
    const width = this.handleWidthFetch(element);

    // Set width via state.
    this.setState({
      width,
    });
  };

  /**
   * Save the term that was searched in SearchFilter (Child).
   *
   * @param {string} searchedTerm - searched term.
   */
  setSearchedTerm = (searchedTerm) => this.setState({ searchedTerm });

  /**
   * Filter elections results based on the searched term.
   *
   * @param {array} results - results to filter.
   */
  filterResults = (results) => {
    const { searchedTerm } = this.state;
    return searchedTerm
      ? results.filter(
        (result) => result.title === searchedTerm
        || result.subtitle === searchedTerm
      )
      : results;
  };

  /**
   * Send a custom event for data tracking when a election hub
   * is visible in the viewport.
   *
   * @memberof ElectionHubWidget
   */
  sendDisplayTrackingEvent() {
    const { title, widgetId } = this.props;
    const element = ReactDOM.findDOMNode(this.hubTarget); // eslint-disable-line react/no-find-dom-node
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sendCustomEvent({
            eventType: 'eHubDisplay',
            widgetId,
            widgetTitle: title,
          });
          // We only want this event to fire once.
          observer.unobserve(entry.target);
        }
      });
    });
    observer.observe(element);
  }

  /**
   * Render this component.
   * @returns {object} - JSX for this component.
   */
  render() {
    const {
      description = '',
      results = [],
      title = '',
      widgetId,
    } = this.props;

    const {
      width,
    } = this.state;

    // Race Styled Components.
    const {
      ElectionHub,
      ElectionHubTitle,
      ElectionHubDescription,
      GlobalStyles,
    } = styles;

    // Larger than main content.
    const isMax = HUB_MAIN_CONTENT < Math.round(width);
    const maxCount = isMax && 1 < results.length;
    const resultCount = 4 <= results.length ? 4 : results.length;

    // Is larger than sidebar.
    const isMain = 300 < width && HUB_MAIN_CONTENT >= width;

    // at or less than sidebar.
    // or is main and less than 620 (main content smaller screens).
    const isMin = 300 >= width || maxCount || (isMain && 620 >= width);

    // If groups exist in this widget.
    const hasGroups = hasResultsGroups(results);

    // Slice results if there are more than MAX_RESULT_COUNT for hubs.
    // Assumes initial screen size vs variable screen size.
    const visibleResults = ! hasGroups && isInNXSWidgetArea(this.hubTarget)
      ? results.slice(0, MAX_RESULT_COUNT) : results;

    // Filter all results that match the search term
    const filteredResults = this.filterResults(visibleResults);

    return (
      <ElectionHub
        className={classNames(
          'election-hub-widget-embed',
          {
            'election-hub--min': isMin,
            'election-hub--main': isMain,
            'election-hub--max': isMax,
            'election-hub--has-groups': hasGroups,
          },
          `election-hub--count-${resultCount}`
        )}
        ref={(node) => { if (null !== node) { this.hubTarget = node; } }}
      >
        <GlobalStyles />
        {0 < title.length && (
          <ElectionHubTitle
            className="election-hub-widget-title"
          >
            {DOMPurify.sanitize(title)}
          </ElectionHubTitle>
        )}
        {0 < description.length && (
          <ElectionHubDescription
            className="election-hub-widget-description"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ // phpcs:disable WordPressVIPMinimum.JS.DangerouslySetInnerHTML.Found
              __html: DOMPurify.sanitize(description),
            }}
          />
        )}
        {filteredResults && hasGroups && (
          <RaceGroupNav
            results={filteredResults}
          />
        )}
        { 5 < visibleResults.length && (
          <SearchFilter
            results={visibleResults}
            setSearchedTerm={this.setSearchedTerm}
            widgetTitle={title}
            widgetId={widgetId}
          />
        )}
        {filteredResults.map((result) => {
          switch (result.type) {
            case 'measure':
              return (
                <MeasureCard
                  key={JSON.stringify(result)}
                  {...result}
                />
              );
            case 'race':
              return (
                <RaceCard
                  key={JSON.stringify(result)}
                  {...result}
                />
              );
            case 'group':
              return (
                <RaceGroup
                  key={result.title}
                  {...result}
                />
              );
            default:
              return null;
          }
        })}
      </ElectionHub>
    );
  }
}
